import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// ════════════════════════════════════════════════════════════════════════════
// Workspace-mode + own-box-consent FSM store — BYO-box relay (#1062),
// Phase 2-frontend (mode chooser) + Phase 3 (consent recording).
//
// Applies the workspace Frontend FSM pattern (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — two phase unions + the exported TRANSITIONS table (R-A1).
//   2. Origin-keyed store  — Map<userEmail, WorkspaceModeState> with persist. The origin is
//                            the USER, not the chat session: `workspaceMode` is a property of
//                            the person's account (which machine is MY dev box), so keying by
//                            session would let two tabs of the same user disagree about
//                            whether they consented to us SSHing into their laptop.
//   3. Server-authoritative — reconcile(email, status) from GET /workspace is the ONLY truth
//                            signal. The store never synthesizes "consented" locally, and a
//                            persisted `granted` is demoted on rehydrate — a consent read out
//                            of localStorage is not a consent.
//   4. Atomic transitions  — one user intent = one named action; the two regions are
//                            ORTHOGONAL (mode ⊥ consent) and cross-region effects are applied
//                            in a single setState so a render can never observe a half-move.
//   5. Setpoint evals      — setpoint-evals/byo-box-mode-consent/ drives THIS store.
//
// The wire is REST, not Socket.IO (like devPreviewStore, unlike sshTerminalStore):
//   GET    /workspace         → reconcile
//   POST   /workspace/mode    → modeChanged / modeChangeFailed
//   POST   /workspace/consent → consentGranted / consentFailed
//   DELETE /workspace/consent → consentRevoked / revokeFailed
// The components are the wire adapters (they call authFetch); this store only owns state.
//
// WHY TWO REGIONS: mode and consent are genuinely independent axes. A user can be on
// `managed` while still holding a valid own-box consent (they switched back temporarily),
// and can be on `own-box` with no consent at all (the fail-closed state the gateway
// enforces). Collapsing them into one union would need a state per pair and would make the
// illegal-by-construction pairs representable anyway.
// ════════════════════════════════════════════════════════════════════════════

// ─── State types ───────────────────────────────────────────────────────────────

/**
 * Mode region.
 *
 *   unknown    not yet reconciled with the server — render nothing decisive.
 *   managed    our provisioned sandbox is this user's dev box (the default).
 *   own-box    the user's own machine (LAN or Tailscale 100.x). Reaching it ALSO needs
 *              consent + config; being in this phase does not imply either.
 *   switching  POST /workspace/mode in flight.
 *   error      the last mode change failed. lastModeError carries the message.
 */
export type WorkspaceModePhase = 'unknown' | 'managed' | 'own-box' | 'switching' | 'error';

/**
 * Consent region — whether the user has granted backend→own-box SSH access at the CURRENT
 * copy version.
 *
 *   unknown     not yet reconciled.
 *   absent      no consent on file, or the accepted version is stale (re-consent needed).
 *   prompting   the consent modal is open, awaiting an explicit accept.
 *   recording   POST /workspace/consent in flight.
 *   granted     server confirmed a current consent — the own-box target can resolve.
 *   revoking    DELETE /workspace/consent in flight (tears down key + config too).
 *   error       the last consent/revoke call failed.
 */
export type ConsentPhase =
  | 'unknown'
  | 'absent'
  | 'prompting'
  | 'recording'
  | 'granted'
  | 'revoking'
  | 'error';

export interface WorkspaceModeState {
  modePhase: WorkspaceModePhase;
  consentPhase: ConsentPhase;
  /** Is an own-box SSH config saved? Independent of mode — survives switching to managed. */
  configured: boolean;
  /** Own-box host as last reported by the server (display only; never a connection source). */
  host: string | null;
  port: number | null;
  /** ISO timestamp of the accepted consent, or null. Metadata — never the gate. */
  consentAcceptedAt: string | null;
  /** Version the user accepted vs the version the server now requires. A mismatch is why
   *  `consentPhase` can be `absent` while `consentAcceptedAt` is non-null. */
  consentVersion: string | null;
  consentCurrentVersion: string | null;
  lastModeError: string | null;
  lastConsentError: string | null;
}

export interface WorkspaceModeStoreShape {
  /** Origin-keyed: one entry per user (email). */
  users: Map<string, WorkspaceModeState>;
}

/**
 * Server-authoritative reconcile payload — mirrors the backend `WorkspaceStatus` interface
 * (backend/src/voice/workspace.service.ts) field-for-field. SE-06 (wire-drift, R-A5 adapted
 * to REST) asserts the two stay in lockstep so the wire cannot drift ahead of the store.
 */
export interface WorkspaceStatusWire {
  configured: boolean;
  type?: string;
  host?: string;
  port?: number;
  user?: string;
  reposPath?: string;
  mode: 'managed' | 'own-box' | 'dedicated';
  consentAcceptedAt: string | null;
  consentVersion: string | null;
  consentCurrentVersion: string;
  consentSatisfied: boolean;
}

/** Result of POST /workspace/mode. */
export interface ModeChangeResult {
  ok: boolean;
  mode: 'managed' | 'own-box';
  consentRequired: boolean;
  configRequired: boolean;
}

// ─── Default state factory ──────────────────────────────────────────────────────

function defaultWorkspaceModeState(): WorkspaceModeState {
  return {
    modePhase: 'unknown',
    consentPhase: 'unknown',
    configured: false,
    host: null,
    port: null,
    consentAcceptedAt: null,
    consentVersion: null,
    consentCurrentVersion: null,
    lastModeError: null,
    lastConsentError: null,
  };
}

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// The canonical FSM. SE-02 validates that every distinct `trigger` here is covered by at
// least one simulator scenario's `// SE-COVERS:` marker — adding a transition MUST add a
// covering scenario. Each trigger is a NAMED store action or a NAMED reconcile outcome,
// never an "anything else" path (R-A2). `region` makes the orthogonality explicit.

export const TRANSITIONS: Array<{
  region: 'mode' | 'consent';
  from: WorkspaceModePhase | ConsentPhase;
  to: WorkspaceModePhase | ConsentPhase;
  trigger: string;
}> = [
  // ── Mode region ──
  { region: 'mode', from: 'unknown', to: 'managed', trigger: 'reconcile:managed' },
  { region: 'mode', from: 'own-box', to: 'managed', trigger: 'reconcile:managed' },
  { region: 'mode', from: 'unknown', to: 'own-box', trigger: 'reconcile:own-box' },
  { region: 'mode', from: 'managed', to: 'own-box', trigger: 'reconcile:own-box' },
  { region: 'mode', from: 'managed', to: 'switching', trigger: 'requestMode' },
  { region: 'mode', from: 'own-box', to: 'switching', trigger: 'requestMode' },
  { region: 'mode', from: 'error', to: 'switching', trigger: 'requestMode' },
  { region: 'mode', from: 'switching', to: 'managed', trigger: 'modeChanged:managed' },
  { region: 'mode', from: 'switching', to: 'own-box', trigger: 'modeChanged:own-box' },
  { region: 'mode', from: 'switching', to: 'error', trigger: 'modeChangeFailed' },
  { region: 'mode', from: 'error', to: 'unknown', trigger: 'dismissModeError' },

  // ── Consent region ──
  { region: 'consent', from: 'unknown', to: 'granted', trigger: 'reconcile:consent-granted' },
  { region: 'consent', from: 'absent', to: 'granted', trigger: 'reconcile:consent-granted' },
  { region: 'consent', from: 'unknown', to: 'absent', trigger: 'reconcile:consent-absent' },
  { region: 'consent', from: 'granted', to: 'absent', trigger: 'reconcile:consent-absent' },
  { region: 'consent', from: 'absent', to: 'prompting', trigger: 'openConsent' },
  { region: 'consent', from: 'prompting', to: 'absent', trigger: 'cancelConsent' },
  { region: 'consent', from: 'prompting', to: 'recording', trigger: 'submitConsent' },
  { region: 'consent', from: 'recording', to: 'granted', trigger: 'consentGranted' },
  { region: 'consent', from: 'recording', to: 'error', trigger: 'consentFailed' },
  { region: 'consent', from: 'granted', to: 'revoking', trigger: 'requestRevoke' },
  { region: 'consent', from: 'revoking', to: 'absent', trigger: 'consentRevoked' },
  { region: 'consent', from: 'revoking', to: 'error', trigger: 'revokeFailed' },
  { region: 'consent', from: 'error', to: 'absent', trigger: 'dismissConsentError' },
];

// ─── Map serializer ─────────────────────────────────────────────────────────────
// JSON.stringify/parse do not round-trip Map natively. Mirrors devPreviewStore.ts /
// sshTerminalStore.ts; covered by SE-01.

function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: [...value.entries()] };
  }
  return value;
}

function mapReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as { __type?: string }).__type === 'Map'
  ) {
    return new Map((value as { entries: [unknown, unknown][] }).entries);
  }
  return value;
}

const mapAwareStorage = createJSONStorage(() => localStorage, {
  replacer: mapReplacer,
  reviver: mapReviver,
});

/**
 * Demote every live phase to its un-confirmed form. Used by BOTH `partialize` (what we
 * write) and `onRehydrateStorage` (what we trust on read).
 *
 * `granted` is demoted like everything else, deliberately: a consent to SSH into someone's
 * personal machine must be re-confirmed by the server on every load, never restored from
 * localStorage where a stale (or hand-edited) entry could present as consent the backend
 * would reject. The metadata (host, timestamp, version) IS kept so the panel can render
 * instantly before the reconcile fetch returns.
 */
function demote(s: WorkspaceModeState): WorkspaceModeState {
  return {
    ...s,
    modePhase: 'unknown',
    consentPhase: 'unknown',
    lastModeError: null,
    lastConsentError: null,
  };
}

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useWorkspaceModeStore = create<WorkspaceModeStoreShape>()(
  persist(
    (): WorkspaceModeStoreShape => ({
      users: new Map<string, WorkspaceModeState>(),
    }),
    {
      name: 'va-workspace-mode-v1',
      storage: mapAwareStorage,
      partialize: (state) => ({
        users: new Map([...state.users.entries()].map(([id, s]) => [id, demote(s)])),
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const next = new Map<string, WorkspaceModeState>();
        for (const [id, s] of state.users.entries()) next.set(id, demote(s));
        useWorkspaceModeStore.setState({ users: next });
      },
    },
  ),
);

// ─── Internal helper ─────────────────────────────────────────────────────────────

/** Upsert a user entry through a transform, returning a fresh Map (immutable). */
function patchUser(
  email: string,
  patch: (cur: WorkspaceModeState) => WorkspaceModeState,
): void {
  useWorkspaceModeStore.setState((s) => {
    const users = new Map(s.users);
    const cur = users.get(email) ?? defaultWorkspaceModeState();
    users.set(email, patch(cur));
    return { users };
  });
}

// ─── Actions (one named action per transition / intent — R-A2/R-A4) ─────────────

/**
 * Server-authoritative reconcile (GET /workspace). The SINGLE truth signal for both
 * regions (pattern rule 3).
 *
 * The consent region follows the server's `consentSatisfied` — the result of THE predicate
 * the gateway itself gates on — not a local comparison of version strings. That is what
 * makes "the UI says granted" and "the gateway will resolve the target" the same claim.
 *
 * In-flight phases are left untouched in both regions: a status poll must not clobber a
 * pending write, and an error stays surfaced until explicitly dismissed.
 */
export function reconcile(email: string, status: WorkspaceStatusWire): void {
  patchUser(email, (cur) => {
    const base: WorkspaceModeState = {
      ...cur,
      configured: status.configured,
      host: status.host ?? null,
      port: status.port ?? null,
      consentAcceptedAt: status.consentAcceptedAt,
      consentVersion: status.consentVersion,
      consentCurrentVersion: status.consentCurrentVersion,
    };

    const modeBusy = cur.modePhase === 'switching' || cur.modePhase === 'error';
    const consentBusy =
      cur.consentPhase === 'recording' ||
      cur.consentPhase === 'revoking' ||
      cur.consentPhase === 'prompting' ||
      cur.consentPhase === 'error';

    return {
      ...base,
      // 'dedicated' resolves like managed server-side and is not user-selectable, so it
      // renders as managed rather than inventing a phase the chooser can't act on.
      modePhase: modeBusy ? cur.modePhase : status.mode === 'own-box' ? 'own-box' : 'managed',
      consentPhase: consentBusy ? cur.consentPhase : status.consentSatisfied ? 'granted' : 'absent',
    };
  });
}

/** managed | own-box | error → switching. User picked a mode; the POST is in flight. */
export function requestMode(email: string): void {
  patchUser(email, (cur) =>
    cur.modePhase === 'switching' ? cur : { ...cur, modePhase: 'switching', lastModeError: null },
  );
}

/**
 * switching → managed | own-box (POST /workspace/mode ok).
 *
 * ATOMIC CROSS-REGION TRANSITION: the server's `consentRequired` is applied to the CONSENT
 * region in the same setState as the mode move. Selecting own-box never grants consent, so
 * this can only ever demote `granted` → `absent` (e.g. the copy version was bumped between
 * page load and the click); it never promotes. Splitting this into two actions would let a
 * render land between them and show "own-box, consent granted" for a user the gateway will
 * refuse.
 */
export function modeChanged(email: string, result: ModeChangeResult): void {
  patchUser(email, (cur) => {
    const consentPhase: ConsentPhase =
      result.consentRequired && cur.consentPhase === 'granted' ? 'absent' : cur.consentPhase;
    return {
      ...cur,
      modePhase: result.mode === 'own-box' ? 'own-box' : 'managed',
      consentPhase,
      lastModeError: null,
    };
  });
}

/** switching → error (POST /workspace/mode failed or network error). */
export function modeChangeFailed(email: string, message: string): void {
  patchUser(email, (cur) => ({ ...cur, modePhase: 'error', lastModeError: message }));
}

/** error → unknown (user dismissed; the next reconcile re-establishes truth). */
export function dismissModeError(email: string): void {
  patchUser(email, (cur) =>
    cur.modePhase === 'error' ? { ...cur, modePhase: 'unknown', lastModeError: null } : cur,
  );
}

/** absent → prompting. Open the consent modal. Does NOT grant anything. */
export function openConsent(email: string): void {
  patchUser(email, (cur) =>
    cur.consentPhase === 'absent' ? { ...cur, consentPhase: 'prompting' } : cur,
  );
}

/** prompting → absent. User closed the modal without accepting. */
export function cancelConsent(email: string): void {
  patchUser(email, (cur) =>
    cur.consentPhase === 'prompting' ? { ...cur, consentPhase: 'absent' } : cur,
  );
}

/** prompting → recording. User pressed Accept; POST /workspace/consent in flight. */
export function submitConsent(email: string): void {
  patchUser(email, (cur) =>
    cur.consentPhase === 'prompting'
      ? { ...cur, consentPhase: 'recording', lastConsentError: null }
      : cur,
  );
}

/** recording → granted (POST /workspace/consent ok). */
export function consentGranted(
  email: string,
  result: { acceptedAt: string; version: string },
): void {
  patchUser(email, (cur) => ({
    ...cur,
    consentPhase: 'granted',
    consentAcceptedAt: result.acceptedAt,
    consentVersion: result.version,
    lastConsentError: null,
  }));
}

/** recording → error. */
export function consentFailed(email: string, message: string): void {
  patchUser(email, (cur) => ({ ...cur, consentPhase: 'error', lastConsentError: message }));
}

/** granted → revoking. DELETE /workspace/consent in flight. */
export function requestRevoke(email: string): void {
  patchUser(email, (cur) =>
    cur.consentPhase === 'granted'
      ? { ...cur, consentPhase: 'revoking', lastConsentError: null }
      : cur,
  );
}

/**
 * revoking → absent (DELETE /workspace/consent ok).
 *
 * ATOMIC CROSS-REGION TRANSITION: revocation tears down the stored key + config and drops
 * the user back to `managed` server-side (AC5), so the mode region, `configured`, and the
 * host metadata all move in the SAME setState. Leaving `configured: true` or `own-box`
 * behind would render a box the user just disconnected.
 */
export function consentRevoked(email: string): void {
  patchUser(email, (cur) => ({
    ...cur,
    consentPhase: 'absent',
    modePhase: 'managed',
    configured: false,
    host: null,
    port: null,
    consentAcceptedAt: null,
    consentVersion: null,
    lastConsentError: null,
  }));
}

/** revoking → error. */
export function revokeFailed(email: string, message: string): void {
  patchUser(email, (cur) => ({ ...cur, consentPhase: 'error', lastConsentError: message }));
}

/** error → absent. */
export function dismissConsentError(email: string): void {
  patchUser(email, (cur) =>
    cur.consentPhase === 'error'
      ? { ...cur, consentPhase: 'absent', lastConsentError: null }
      : cur,
  );
}

// ─── Non-hook accessors (for effect bodies / event handlers) ────────────────────

export function getWorkspaceMode(
  state: WorkspaceModeStoreShape,
  email: string | null,
): WorkspaceModeState | undefined {
  if (!email) return undefined;
  return state.users.get(email);
}

// ─── Hook selectors — the ONLY supported way to read state in React ─────────────

/** Subscribe to a user's full workspace-mode entry (default when absent). */
export function useWorkspaceModeEntry(email: string | null): WorkspaceModeState {
  return useWorkspaceModeStore(
    useShallow((s) =>
      email ? s.users.get(email) ?? defaultWorkspaceModeState() : defaultWorkspaceModeState(),
    ),
  );
}

/** Subscribe to just the mode-region phase. */
export function useWorkspaceModePhase(email: string | null): WorkspaceModePhase {
  return useWorkspaceModeStore((s) =>
    email ? s.users.get(email)?.modePhase ?? 'unknown' : 'unknown',
  );
}

/** Subscribe to just the consent-region phase. */
export function useConsentPhase(email: string | null): ConsentPhase {
  return useWorkspaceModeStore((s) =>
    email ? s.users.get(email)?.consentPhase ?? 'unknown' : 'unknown',
  );
}
